import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal, type Signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DiagnosticRating, MaloupeLocale } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { RatingChipComponent } from ':modules/rating-chip/rating-chip.component';
import {
    EXPERT_DIAGNOSTIC_QUICK_CALL_EN,
    EXPERT_DIAGNOSTIC_QUICK_CALL_FR,
    REVIEW_CALCULATOR_REDIRECT_EN_URL,
    REVIEW_CALCULATOR_REDIRECT_FR_URL,
} from ':shared/constants';
import { Icon } from ':shared/enums';
import {
    Illustration,
    IllustrationPathResolverPipe,
    ImagePathResolverPipe,
    LogoPathResolverPipe,
    PluralTranslatePipe,
} from ':shared/pipes';

@Component({
    selector: 'app-diagnostic-competitors',
    imports: [
        NgClass,
        NgTemplateOutlet,
        TranslateModule,
        RatingChipComponent,
        IllustrationPathResolverPipe,
        ImagePathResolverPipe,
        PluralTranslatePipe,
        LogoPathResolverPipe,
    ],
    templateUrl: './diagnostic-competitors.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticCompetitorsComponent {
    readonly diagnostic = input.required<Diagnostic>();

    private readonly _translateService = inject(TranslateService);
    private readonly _partnerThemeContext = inject(PartnerThemeContext);

    readonly partnerTheme = this._partnerThemeContext.activeTheme;

    readonly Illustration = Illustration;
    readonly DiagnosticRating = DiagnosticRating;
    readonly Icon = Icon;
    readonly EXPERT_DIAGNOSTIC_QUICK_CALL_FR = EXPERT_DIAGNOSTIC_QUICK_CALL_FR;
    readonly EXPERT_DIAGNOSTIC_QUICK_CALL_EN = EXPERT_DIAGNOSTIC_QUICK_CALL_EN;

    readonly locale: MaloupeLocale = this._translateService.currentLang as MaloupeLocale;
    readonly isEnglish = signal<boolean>(this._translateService.currentLang === MaloupeLocale.EN);

    readonly restaurantName = computed(() => this.diagnostic().restaurant.name);

    readonly googleDiagnosticRating: Signal<DiagnosticRating> = computed(() => this.diagnostic().getGoogleRating() ?? DiagnosticRating.BAD);
    readonly googleReviewCountDiagnosticRating: Signal<DiagnosticRating> = computed(
        () => this.diagnostic().getReviewCountRating() ?? DiagnosticRating.BAD
    );

    readonly googleDiagnosticResult = computed(() => ({
        rating: this.diagnostic().restaurant?.rating,
        reviewCount: this.diagnostic().reviewCount,
        averageReviewCountForSimilarRestaurants: this.diagnostic().averageReviewCountForSimilarRestaurants,
        averageRatingForSimilarRestaurants: this.diagnostic().restaurant?.averageRatingForSimilarRestaurants,
        restaurantWithHigherRatingCount: this.diagnostic().getBetterLocalCompetitorCount() ?? 0,
    }));

    redirectToReviewCalculator(): void {
        const url = this.locale === MaloupeLocale.EN ? REVIEW_CALCULATOR_REDIRECT_EN_URL : REVIEW_CALCULATOR_REDIRECT_FR_URL;
        window.open(url, '_blank');
    }
}
